import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import styles from "./EditCard.module.scss";
import { useLayoutPropsUtil } from "../../hooks";
import { Card } from "../Card";
import { Text } from "../Text";
import { Icon } from "../Icon";
import { Button } from "../Button";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import PriorityHigh from "@servicetitan/hammer-icon/mdi/round/priority_high.svg";

/**
 * The current state of the edit card.
 */
export type EditCardState =
  | "not started"
  | "in progress"
  | "error"
  | "complete";

/**
 * The type of state change that can occur.
 */
export type EditCardChange = "edit" | "save" | "cancel";

/**
 * Props for the EditCard component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type EditCardProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * The header text to display. Can be a string or an object mapping states to different text.
     */
    headerText:
      | string
      | {
          "not started": string;
          "in progress": string;
          error: string;
          complete: string;
        };
    /**
     * The current state of the edit card.
     */
    state: EditCardState;
    /**
     * Text to display on the save button.
     * @default "Save"
     */
    saveButtonText?: string;
    /**
     * Text to display on the edit button.
     * @default "Edit"
     */
    editButtonText?: string;
    /**
     * Whether to disable the edit button.
     * @default false
     */
    disableEdit?: boolean;
    /**
     * Whether to disable the save button.
     * @default false
     */
    disableSave?: boolean;
    /**
     * Callback function called when the state changes.
     */
    onStateChange?: (changeType: EditCardChange) => void;
  };

/**
 * EditCard component for managing editable content with different states and actions.
 *
 * Features:
 * - Four distinct states: not started, in progress, error, and complete
 * - Visual indicators for each state with appropriate icons
 * - Dynamic header text that can change based on state
 * - Edit and save buttons with customizable text
 * - Optional button disabling for different states
 * - State change callbacks for handling user interactions
 * - Built on Card component with consistent styling
 * - Supports layout utilities for positioning and spacing
 * - Accessible with proper ARIA attributes
 * - Visual state indicators with color-coded borders
 *
 * @example
 * <EditCard
 *   headerText="User Profile"
 *   state="not started"
 *   onStateChange={(change) => console.log(change)}
 * >
 *   <Text>Profile information goes here</Text>
 * </EditCard>
 */
export const EditCard = forwardRef<HTMLDivElement, EditCardProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      style,
      headerText,
      state,
      editButtonText = "Edit",
      saveButtonText = "Save",
      disableEdit = false,
      disableSave = false,
      onStateChange,
      ...rest
    } = componentProps;

    const kebabState = state?.replaceAll(" ", "-");

    const editCardClassNames = cx(
      styles["edit-card"],
      styles[`edit-card-${kebabState}`],
      className,
    );

    return (
      <Card
        padding="large"
        className={editCardClassNames}
        data-anv="edit-card"
        style={style}
        ref={ref}
        {...rest}
      >
        <div
          className={cx(styles.indicator, styles[`indicator-${kebabState}`])}
        >
          {state === "complete" && <Icon svg={Check} />}
          {state === "error" && <Icon svg={PriorityHigh} />}
        </div>
        <div className={styles.container}>
          <div className={styles.header}>
            <Text className={styles.heading} variant="headline" el="h3">
              {typeof headerText === "string" ? headerText : headerText[state]}
            </Text>
            {state !== "in progress" && (
              <Button
                disabled={disableEdit}
                onClick={() => onStateChange?.("edit")}
              >
                {editButtonText}
              </Button>
            )}
          </div>
          {children && (
            <div className={styles.content} style={layoutStyles}>
              {children}
            </div>
          )}
          {state === "in progress" && (
            <div className={styles.footer}>
              <Button onClick={() => onStateChange?.("cancel")}>Cancel</Button>
              <Button
                appearance="primary"
                onClick={() => onStateChange?.("save")}
                disabled={disableSave}
              >
                {saveButtonText}
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  },
);

EditCard.displayName = "EditCard";
